import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { parseISO } from "date-fns";

import { Info, User } from "components/elements";
import { GrabsonIcon, TableRowColumn, TableSubRow } from "components/layout";
import { tivoAppsApi } from "containers/Request/SRPC/apiClient";
import {
  RELEASE_STAGES,
  ReleaseStage,
  TiVoPublicationHistoryEntry,
  TiVoPublicationHistoryRow,
  TiVoPublicationHistoryState,
} from "hooks/tivoApps";
import { parseLocationToQueryParams } from "pages/TiVoApps/utils";
import { trans } from "src/translations";
import { formatDateTime } from "utils/date";

import { PAGE_LIMIT, TEXTS } from "../constants";
import styles from "./PublicationHistoryPanel.scss";
import tableStyles from "./PublicationsTable.scss";
import { PublicationsTableRowPanelProps } from "./PublicationsTableRow";
import { PublicationStatus, PublicationStatusProps } from "./PublicationStatus";

const BASE_COL_COUNT = 3;

export type PublicationHistoryPanelProps = Omit<
  PublicationsTableRowPanelProps,
  "publication"
> & {
  limit?: number;
};

function inferPublicationStatusProps(
  releaseStage: ReleaseStage,
  state: TiVoPublicationHistoryState
): PublicationStatusProps {
  const props: PublicationStatusProps = { releaseStage };
  switch (state) {
    case "published":
      props.publication = [releaseStage];
      props.variantReleaseStages = [releaseStage];
      props.applicableReleaseStages = [releaseStage];
      break;
    case "promotedNotTargeted":
      props.variantReleaseStages = [releaseStage];
      props.applicableReleaseStages = [releaseStage];
      props.isTargetValid = false;
      break;
    case "promotedTargetedNotPublished":
      props.variantReleaseStages = [releaseStage];
      props.applicableReleaseStages = [releaseStage];
      break;
    case "notPromoted":
      props.applicableReleaseStages = [releaseStage];
      break;
    case "notPromotableOemRestricted":
      break;
  }
  return props;
}

function renderStatus(
  oemId: string,
  clientTypeId: string | undefined,
  entries: TiVoPublicationHistoryRow["entries"]
) {
  let entry: TiVoPublicationHistoryEntry | undefined;
  if (clientTypeId) {
    entry = entries.find((e) => e.clientTypeId === clientTypeId);
  } else {
    entry = entries.find((e) => e.oemId === oemId);
  }
  if (!entry) {
    return null;
  }
  return RELEASE_STAGES.map((releaseStage) => {
    return (
      <PublicationStatus
        key={releaseStage}
        {...inferPublicationStatusProps(
          releaseStage,
          entry.releaseStages[releaseStage]
        )}
      />
    );
  });
}

function renderUser(modifiedBy: TiVoPublicationHistoryRow["modifiedBy"]) {
  if (!modifiedBy) {
    return (
      <div className={styles.automatic}>
        <GrabsonIcon name="device" className={styles.icon} size="normal" />
        <span className={styles.label}>{TEXTS.AUTOMATIC}</span>
      </div>
    );
  }
  return <User userId={modifiedBy.id} />;
}

export function PublicationHistoryPanel({
  variant,
  platformId,
  oems,
  limit = PAGE_LIMIT,
  getOemClientTypes,
}: PublicationHistoryPanelProps) {
  const location = useLocation();
  const { modifiedAfter, modifiedBefore } =
    parseLocationToQueryParams(location);
  const {
    data,
    isLoading: loading,
    error,
  } = tivoAppsApi.getTiVoVariantPublicationHistory.useQuery(variant.id, {
    queryParams: {
      include: ["modifiedBy"],
      limit,
      modifiedAfter,
      modifiedBefore,
      order: "-modificationDate",
      platformId,
    },
  });

  const count = data?.meta?.total ?? 0;
  const rows = data?.results ?? [];

  const messageColSpan = useMemo(
    () =>
      oems.reduce(
        (count, oem) =>
          count +
          (getOemClientTypes(oem).length > 0
            ? getOemClientTypes(oem).length
            : 1),
        BASE_COL_COUNT - 1
      ),
    [oems, getOemClientTypes]
  );

  const renderMessage = useCallback(
    (message: string, error = false, dataTestId = "") => (
      <TableSubRow className={styles.row} highlighting={false}>
        <TableRowColumn columnClass={styles.message} colspan={messageColSpan}>
          {error ? (
            <Info
              type="error"
              className={styles.error}
              dataTestId={dataTestId || "message-no-results"}
            >
              {message}
            </Info>
          ) : (
            <div data-test-id={dataTestId || "message-no-results-empty"}>
              {message}
            </div>
          )}
        </TableRowColumn>
        <TableRowColumn columnClass={tableStyles.menuCell} />
      </TableSubRow>
    ),
    [messageColSpan]
  );

  if (loading) {
    return renderMessage(`${trans.LOADING()}...`);
  }

  if (error) {
    return renderMessage(error.message, true);
  }

  if (rows.length === 0) {
    return renderMessage(TEXTS.NO_PUBLICATION_HISTORY);
  }

  return (
    <>
      {rows.map((row) => (
        <TableSubRow key={row.id} className={styles.row}>
          <TableRowColumn colspan={2} columnClass={styles.detailsCell}>
            <div className={styles.details}>
              <div className={styles.user}>{renderUser(row.modifiedBy)}</div>
              <div className={styles.dateTime}>
                {formatDateTime(parseISO(String(row.modificationDate)))}
              </div>
            </div>
          </TableRowColumn>
          {oems.map((oem) => {
            const clientTypes =
              getOemClientTypes(oem).length > 0
                ? getOemClientTypes(oem)
                : [undefined];
            return clientTypes.map((clientType) => (
              <TableRowColumn key={`${oem.id}-${clientType?.id || "all"}`}>
                {renderStatus(oem.id, clientType?.id, row.entries)}
              </TableRowColumn>
            ));
          })}
          <TableRowColumn columnClass={tableStyles.menuCell} />
        </TableSubRow>
      ))}
      {count > limit &&
        renderMessage(
          TEXTS.PUBLICATION_HISTORY_LIMITED.replace(
            "#{count}",
            String(count - limit)
          ),
          false,
          "message-results-limited"
        )}
    </>
  );
}
